'use client';

import { useEffect } from 'react';
import Cookies from 'js-cookie';
import { useInitLanding } from '@/api/useInitLanding';
import { registerInvite } from '@/api/invite/useInviteCode';
import { readInviteParams } from '@/lib/invite';
import { generateUUID } from '@/lib/polyfills';
import { markVisitReady } from '@/lib/visit';

/**
 * Una sola cattura per caricamento di pagina. Il cookie si scrive dopo la
 * risposta di init, quindi un secondo passaggio prima di quella risposta non
 * lo trova e crea un'altra visita: succede in dev a ogni pagina (StrictMode
 * esegue l'effetto due volte) e in produzione a ogni rimontaggio. Il flag
 * sta nel modulo perché sopravvive al rimontaggio del componente.
 */
let hasCaptured = false;

export default function TpSessionHandler() {
  const { initLanding } = useInitLanding();

  useEffect(() => {
    if (hasCaptured) return;
    hasCaptured = true;

    const captureTrafficData = async () => {
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');
      const utmContent = params.get('utm_content');
      const utmTerm = params.get('utm_term');
      const adGroup = params.get('adgroup');
      const matchType = params.get('matchtype');
      const referrer = document.referrer || null;
      // La pagina di atterraggio, senza dominio e senza query: le UTM hanno già
      // i loro campi, e il resto della query non ci serve. Va letta qui, nello
      // stesso istante delle UTM, perché è questo il momento che descrive la
      // visita: dopo il cookie non si passa più di qui per un anno.
      const landingPath = window.location.pathname;
      // Codice invito (/invito/CODE o `?ref=`, più `?ch=`), da qualunque pagina: chi lo riceve non
      // atterra per forza su /invito, e il codice non va perso per strada.
      const invite = readInviteParams(window.location.pathname, window.location.search);

      // Basic device type detection
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /iphone|ipad|ipod|android|blackberry|mini|windows\sce|palm/i.test(userAgent);
      const deviceType = isMobile ? 'mobile' : 'desktop';

      // Set a cookie and proceed only if not already existing
      const existingSessionId = Cookies.get(
        process.env.NEXT_PUBLIC_TRAFFIC_SOURCES_COOKIE_NAME || 'trovanorma_tpSessionId'
      );
      if (existingSessionId) {
        // Visita già tracciata: init non si ripete, quindi l'invito si
        // aggancia alla riga esistente con la sua chiamata. Vince l'ultimo
        // invito. Gli errori si ignorano: il codice viaggia comunque
        // sull'URL di registrazione.
        if (invite) {
          registerInvite({ tpSessionId: existingSessionId, ...invite }).catch(() => {});
        }
        markVisitReady(existingSessionId);
        return;
      }
      const tpSessionId = generateUUID();

      const trafficData = {
        tpSessionId,
        deviceType,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
        adGroup,
        matchType,
        referrer,
        landingPath,
        ...(invite ? { inviteCode: invite.code, inviteChannel: invite.channel } : {}),
      };

      try {
        // `useApiCall` non lancia: su errore restituisce null. Senza risposta
        // il cookie non si scrive, così la pagina successiva riprova init:
        // un cookie che il backend non conosce varrebbe un anno senza visita
        // (niente attribuzione, niente aggancio del codice invito).
        const saved = await initLanding(trafficData);
        if (saved === null) {
          markVisitReady(null);
          return;
        }
        // Set cookies only after they have been sent to server
        Cookies.set(
          process.env.NEXT_PUBLIC_TRAFFIC_SOURCES_COOKIE_NAME || 'trovanorma_tpSessionId',
          tpSessionId,
          {
            expires: 365,
            domain: process.env.NEXT_PUBLIC_TRAFFIC_SOURCES_COOKIE_DOMAIN || '.trovanorma.it',
          }
        );
        Cookies.set(
          process.env.NEXT_PUBLIC_UTM_SOURCE_COOKIE_NAME || 'trovanorma_initialUtmSource',
          utmSource || 'direct',
          {
            expires: 30,
            domain: process.env.NEXT_PUBLIC_TRAFFIC_SOURCES_COOKIE_DOMAIN || '.trovanorma.it',
          }
        );
        markVisitReady(tpSessionId);
      } catch (error) {
        markVisitReady(null);
        console.error('Error initializing TPSession:', error);
      }
    };

    //if (!sessionStorage.getItem('trovanorma_initialTPSession')) {
    captureTrafficData();
    //sessionStorage.setItem('trovanorma_initialTPSession', 'true');
    //}
  }, [initLanding]);

  return null; // This component doesn't render any UI
}
